import { useState, useEffect } from "react";
import supabase from "../../supabase";
import "./InviteLogin.css";
import logo from '../../assets/logo.svg';

export default function InviteLogin() {
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState("");

  // For request access form
  const [showRequestForm, setShowRequestForm] = useState(false);
  const [requestEmail, setRequestEmail] = useState("");
  const [company, setCompany] = useState("");
  const [requestLoading, setRequestLoading] = useState(false);
  const [requestMsg, setRequestMsg] = useState("");

  useEffect(() => {
    const { subscription } = supabase.auth.onAuthStateChange(
      async (event, session) => {
        if (event === "SIGNED_IN" && session?.user?.email) {
          const userEmail = session.user.email;
          try {
            await supabase.rpc("increment_login_count", {
              user_email: userEmail,
            });
          } catch (err) {}
        }
      }
    );
    return () => subscription?.unsubscribe();
  }, []);

  // Login Handler
  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    setMessage("");
    try {
      const { data, error } = await supabase
        .from("allowed_users")
        .select("id, is_active")
        .eq("email", email)
        .maybeSingle();
      if (error) throw error;
      if (!data) {
        setMessage("❌ Request access first");
        setLoading(false);
        return;
      }
      if (!data.is_active) {
        setMessage("⚠ Your access is inactive, contact admin.");
        setLoading(false);
        return;
      }
      const { error: signInError } = await supabase.auth.signInWithOtp({
        email,
        options: {
          emailRedirectTo: window.location.origin + "/dashboard",
        },
      });
      if (signInError) {
        setMessage("❌ " + signInError.message);
      } else {
        setMessage("✅ Magic link sent! Check your email.");
      }
    } catch (err) {
      setMessage("❌ " + err.message);
    } finally {
      setLoading(false);
    }
  };

  // Request Access Handler with duplicate check
  const handleRequestAccess = async (e) => {
    e.preventDefault();
    setRequestLoading(true);
    setRequestMsg("");
    try {
      const { data: existing } = await supabase
        .from('allowed_users')
        .select('id')
        .eq('email', requestEmail)
        .maybeSingle();
      if (existing) {
        setRequestMsg("⚠ You have already requested access with this email. Please wait for admin approval or contact support if you need help.");
      } else {
        const { error } = await supabase
          .from('allowed_users')
          .insert([{
            email: requestEmail,
            company_name: company,
            is_active: false,
          }]);
        if (error) setRequestMsg("❌ " + error.message);
        else setRequestMsg("✅ Request submitted! Admin will review.");
      }
    } catch (err) {
      setRequestMsg("❌ " + err.message);
    } finally {
      setRequestLoading(false);
    }
  };

  return (
    <div className="login-container">
      <div className="login-left">
        <div className="branding-content">
          <div className="logo-section">
            <div className="logo-container">
              <img src={logo} className="logo-image" alt="logo" />
            </div>
            <h1 className="main-title">Business Operations Support System</h1>
            <div className="title-underline"></div>
          </div>
          <h2 className="subtitle">Your one stop Business Solution</h2>
          <div className="creator-info">
            <span className="creator-name"></span>
          </div>
        </div>
      </div>

      <div className="login-right">
        <div className="login-form-container">

          <div className="form-header">
            <h3 className="form-title">Welcome BOSS</h3>
            <p className="form-subtitle">Sign in to access your dashboard</p>
          </div>

          {/* SMOOTH FORM SWITCH */}
          <div className="form-switch-group">
            <form
              onSubmit={handleLogin}
              className={`switch-form ${!showRequestForm ? 'active' : 'inactive'} login-form`}
              autoComplete="on"
            >
              <div className="input-group">
                <input
                  id="email"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="Enter your email address"
                  required
                  className="email-input"
                />
              </div>
              <button
                type="submit"
                disabled={loading}
                className="login-button"
              >
                {loading ? (
                  <span className="loading-content">
                    <span className="loading-spinner"></span>
                    Sending...
                  </span>
                ) : (
                  "Send Magic Link"
                )}
              </button>
              {message && (
                <div className={`message ${message.includes('✅') ? 'success' : message.includes('⚠') ? 'warning' : 'error'}`}>
                  {message}
                </div>
              )}
              <div className="form-footer">
                <p className="help-text">
                  Don't have access? Mail us at <a href="mailto:admin@kliqontech.com">admin@kliqontech.com</a>
                </p>
                
                <p>OR</p>
                <button
                  className="request-access-btn"
                  type="button"
                  onClick={() => setShowRequestForm(true)}
                >
                
                  Request Access
                </button>
              </div>
            </form>

            <form
              onSubmit={handleRequestAccess}
              className={`switch-form ${showRequestForm ? 'active' : 'inactive'} request-access-form`}
              autoComplete="on"
            >
              <div className="input-group">
                <input
                  type="email"
                  required
                  value={requestEmail}
                  onChange={e => setRequestEmail(e.target.value)}
                  placeholder="Your email"
                  className="email-input"
                />
                <input
                  type="text"
                  required
                  value={company}
                  onChange={e => setCompany(e.target.value)}
                  placeholder="Company Name"
                  className="company-input"
                />
              </div>
              <button type="submit" disabled={requestLoading} className="submit-btn">
                {requestLoading ? "Submitting..." : "Submit Request"}
              </button>
              {requestMsg && (
                <div className={`message ${
                  requestMsg.includes('✅') ? 'success'
                    : requestMsg.includes('⚠') ? 'warning'
                    : 'error'
                }`}>
                  {requestMsg}
                </div>
              )}
              <div className="form-footer">
                <button
                  className="request-access-btn"
                  type="button"
                  onClick={() => setShowRequestForm(false)}
                >
                  Back to Login
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>
    </div>
  );
}